import React, { useState, useEffect, useRef } from 'react';

const getInterpolatedColor = (index, total) => {
  // Create a softer transition: light red -> orange -> dark yellow -> green
  // With darker yellows and greens for better contrast with white text
  let r, g, b;
  
  // Normalize the index to a value between 0 and 1
  const t = index / (total - 1);
  
  if (t < 0.33) {
    // Light red to orange (first third)
    const subT = t / 0.33;
    r = 255;
    g = Math.round(165 * subT);
    b = 0;
  } else if (t < 0.66) {
    // Orange to darker yellow (middle third)
    const subT = (t - 0.33) / 0.33;
    r = Math.round(255 - 55 * subT); // Reduce red slightly for darker yellow
    g = Math.round(165 + (200 - 165) * subT); // Cap at 200 instead of 255 for darker yellow
    b = 0;
  } else {
    // Darker yellow to slightly lighter green (last third)
    const subT = (t - 0.66) / 0.34;
    r = Math.round((255 - 55) * (1 - subT));
    g = Math.round(180 - 20 * subT); // Start at 200, go to 160 for slightly lighter green
    b = 0;
  }
  
  return `rgb(${r}, ${g}, ${b})`;
};

const shuffleArray = (arr) => {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
};

// Check if the new position collides with any existing circle
const checkCollision = (newPos, positions, radius) => {
  return positions.some((pos) => {
    const dx = newPos.left - pos.left;
    const dy = newPos.top - pos.top;
    const distance = Math.sqrt(dx * dx + dy * dy);
    return distance < radius * 2.3; // increased spacing (3x radius instead of 2x)
  });
};

// Generate random positions with collision checks
const generateRandomPositions = (count, containerWidth, containerHeight, radius) => {
  const positions = [];
  while (positions.length < count) {
    const newPos = {
      top: Math.random() * (containerHeight - 2 * radius),
      left: Math.random() * (containerWidth - 2 * radius),
    };
    
    // If the new position does not collide with others, add it
    if (!checkCollision(newPos, positions, radius)) {
      positions.push(newPos);
    }
  }
  return positions;
};

const MindFeedbackMatrix = ({saveFeedback = () => {}, title="Click a number from 1 to 10"}) => {
  const [order, setOrder] = useState([]);
  const [selected, setSelected] = useState(null);
  const [positions, setPositions] = useState([]);
  const containerRef = useRef(null);

  useEffect(() => {
    const shuffled = shuffleArray([...Array(10).keys()]?.map(i => i + 1));
    setOrder(shuffled);
    
    // Get the actual container width
    const updatePositions = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        setPositions(generateRandomPositions(10, containerWidth, 400, 40)); // radius of 40 for the circles
      }
    };
    
    // Initial position generation
    updatePositions();
    
    // Update positions on window resize
    window.addEventListener('resize', updatePositions);
    
    // Cleanup
    return () => window.removeEventListener('resize', updatePositions);
  }, []);

  const containerStyle = {
    //minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'sans-serif',
  };

  const headingStyle = {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    marginBottom: '20px',
  };

const messyContainerStyle = {
  position: 'relative',
  width: '80vw', // Ensure it takes full viewport width
  height: '400px',
  //backgroundColor: 'blue',
  //border: '1px dashed #ccc',
  marginBottom: '20px',
  boxSizing: 'border-box', // Include border in the width calculation
};

  const circleStyle = {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    color: 'white',
    fontWeight: 'bold',
    fontSize: '1.2rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    position: 'absolute',
    transition: 'transform 0.2s ease',
  };

  const selectedTextStyle = {
    fontSize: '1.2rem',
  };

  return (
    <div style={containerStyle}>
      <div style={{marginBottom:"20px", padding:"10px", textAlign:"center"}}>
        <h2 style={headingStyle}>{title}</h2>
      </div>
      
      <div ref={containerRef} style={messyContainerStyle}>
        {order?.map((num, index) => (
          <div
            key={num}
            onClick={() => {setSelected(num); saveFeedback(num)}}
            style={{
              ...circleStyle,
              top: positions[index]?.top,
              left: positions[index]?.left,
              backgroundColor: getInterpolatedColor(num - 1, 10),
            }}
            onMouseOver={(e) => (e.currentTarget.style.transform = 'scale(1.1)')}
            onMouseOut={(e) => (e.currentTarget.style.transform = 'scale(1.0)')}
          >
            {num}
          </div>
        ))}
      </div>
      {selected && <p style={selectedTextStyle}>You selected: {selected}</p>}
    </div>
  );
};

export default MindFeedbackMatrix;
